import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;

const d = (offsetDays: number) =>
  new Date(Date.now() + offsetDays * 86_400_000).toISOString();

export async function seedAffitorProject(token: string): Promise<string> {
  const authHeader = { Authorization: `Bearer ${token}` };

  // ── 1. Project ─────────────────────────────────────────────────────────────
  const { data: project } = await axios.post(
    `${BASE}/api/projects`,
    {
      name: "Affitor Campaign — Personal Finance Apps",
      description:
        "Full-funnel affiliate campaign run through all 8 Affitor stages (S1 Research & Discovery → S8 Meta), sourcing and validating programs via list.affitor.com for the personal-finance-app niche.",
      status: "active",
      methodology: "agile",
      sprintLength: 2,

      diagramText: `flowchart TD
  S1[S1: Research and Discovery] --> S2[S2: Content Creation]
  S2 --> S3[S3: Blog and SEO]
  S3 --> S4[S4: Offers and Landing Pages]
  S4 --> S5[S5: Distribution and Deployment]
  S5 --> S6[S6: Analytics and Optimization]
  S6 --> S7{Hit Affitor Score target?}
  S7 -->|Yes| S8[S8: Automation and Scale]
  S7 -->|No| S3
  S8 --> Meta[Meta: Retrospective and Category Design]`,

      stakeholders: [
        {
          id: "sh-1",
          name: "Affiliate Marketer",
          role: "Campaign Owner",
          team: "Growth",
          email: "",
          timezone: "",
          notes: "Runs the campaign end-to-end across all 8 Affitor stages. Owns program selection and go/no-go calls.",
          interest: "Hit target Affitor Score on ≥3 live programs within one quarter",
          influence: "high",
        },
        {
          id: "sh-2",
          name: "Content Team",
          role: "Execution",
          team: "Content",
          email: "",
          timezone: "",
          notes: "Produces S2/S3 output: posts, blog articles, tutorials from the ranked content angles.",
          interest: "Clear angle briefs, no duplicate keyword targets",
          influence: "medium",
        },
        {
          id: "sh-3",
          name: "Affitor Program Manager",
          role: "External Partner",
          team: "list.affitor.com",
          email: "",
          timezone: "",
          notes: "Point of contact for program terms, commission disputes, and cookie-window confirmation.",
          interest: "Quality, compliant traffic from vetted content",
          influence: "medium",
        },
        {
          id: "sh-4",
          name: "SEO Specialist",
          role: "S3/S6 Reviewer",
          team: "Growth",
          email: "",
          timezone: "",
          notes: "Runs seo-audit and content-decay-detector on every published piece.",
          interest: "10-dimension scorecard ≥8/10 average across the site",
          influence: "medium",
        },
        {
          id: "sh-5",
          name: "Compliance Reviewer",
          role: "S8/Meta Gatekeeper",
          team: "Legal",
          email: "",
          timezone: "",
          notes: "Signs off FTC disclosure and platform ToS compliance before any automation/scale stage begins.",
          interest: "Zero disclosure violations at scale",
          influence: "high",
        },
      ],

      raciMatrix: [
        {
          id: "raci-1",
          activity: "Program selection sign-off (S1)",
          assignments: { "sh-1": "A", "sh-2": "I", "sh-3": "C", "sh-4": "I", "sh-5": "I" },
        },
        {
          id: "raci-2",
          activity: "Content publish (S2/S3)",
          assignments: { "sh-1": "A", "sh-2": "R", "sh-3": "I", "sh-4": "C", "sh-5": "C" },
        },
        {
          id: "raci-3",
          activity: "Disclosure and compliance review",
          assignments: { "sh-1": "C", "sh-2": "C", "sh-3": "I", "sh-4": "I", "sh-5": "R" },
        },
        {
          id: "raci-4",
          activity: "Automation and scale go-live (S7→S8)",
          assignments: { "sh-1": "A", "sh-2": "I", "sh-3": "I", "sh-4": "C", "sh-5": "A" },
        },
      ],

      documents: [
        {
          id: "doc-brief",
          type: "brd",
          title: "Affitor Campaign Brief — Personal Finance Apps",
          content: `# Affitor Campaign Brief
**Niche:** Personal Finance Apps
**Stage:** S1 complete, S2-S3 in progress

## Affitor Score
**Overall: 8.1 / 10**

| Dimension | Score | Weight | Weighted |
|---|---|---|---|
| Search Demand | 8 | 30% | 2.4 |
| Program Availability | 9 | 30% | 2.7 |
| Competition Level | 6 | 25% | 1.5 |
| Content Potential | 10 | 15% | 1.5 |

## Programs Sourced from list.affitor.com
1. **Rocket Money** — 25% recurring, 45-day cookie
2. **YNAB (You Need A Budget)** — $20 flat per trial-to-paid, 30-day cookie
3. **Empower (Personal Capital)** — $100 flat CPA, 60-day cookie

## Stage Plan
- S1 Research & Discovery: niche + program validation — **done**
- S2 Content Creation: post/thread/script drafts — **in progress**
- S3 Blog & SEO: comparison + listicle articles — **in progress**
- S4 Offers & Landing Pages: bonus-stacked landing page — **backlog**
- S5 Distribution & Deployment: bio-link + email drip — **backlog**
- S6 Analytics & Optimization: conversion tracking, A/B tests — **backlog**
- S7 Automation & Scale: branching email flows — **backlog**
- S8 Meta: retrospective + category design — **backlog**

## Next Steps
1. Finish comparison-post-writer output for Rocket Money vs YNAB
2. Run seo-audit on both drafts before publish
3. Confirm disclosure placement with Compliance Reviewer before any S5 deployment`,
          createdAt: d(-21),
          updatedAt: d(-2),
        },
        {
          id: "doc-funnel-req",
          type: "frd",
          title: "Funnel Requirements — Distribution & Analytics (S5/S6)",
          content: `# Funnel Requirements — S5/S6
**Stages:** Distribution & Deployment, Analytics & Optimization

## S5: Distribution & Deployment
### FR-DIST-001: Bio Link Ownership
The bio-link-deployer output must resolve to a domain the campaign owns, not a third-party shortener, so link equity and analytics are not lost.

### FR-DIST-002: Email Drip Sequencing
email-drip-sequence must produce 5-7 emails with at least one dedicated to each of the 3 sourced programs.

## S6: Analytics & Optimization
### FR-ANLY-001: UTM Tagging
Every distributed link must carry a UTM set (source, medium, campaign) generated by conversion-tracker before it is published anywhere.

### FR-ANLY-002: A/B Test Minimum Sample
ab-test-generator variants must not be called before a statistically valid sample size (n≥1000 impressions per variant) is reachable within the campaign's traffic estimate.

### FR-ANLY-003: Affitor Score Re-check Cadence
Re-run the Affitor Score for all 3 live programs every 30 days — commission terms and cookie windows are subject to change on list.affitor.com without notice.`,
          createdAt: d(-14),
          updatedAt: d(-1),
        },
      ],

      userStories: [
        {
          id: "us-1",
          title: "Validate niche and source programs (S1)",
          asA: "affiliate marketer",
          iWant: "the personal-finance-app niche scored and 3 programs sourced from list.affitor.com",
          soThat: "I commit budget only to a niche with confirmed program availability",
          acceptanceCriteria: [
            "Affitor Score ≥ 8.0 overall",
            "≥3 programs with commission ≥20% recurring or ≥$20 flat CPA",
            "Cookie window ≥30 days on all sourced programs",
          ],
          priority: "must",
          storyPoints: 5,
          status: "done",
          startDate: d(-21),
          dueDate: d(-16),
          estimatedDays: 5,
        },
        {
          id: "us-2",
          title: "Draft S2 content set for top program",
          asA: "content team member",
          iWant: "viral-post-writer and twitter-thread-writer output for Rocket Money",
          soThat: "S3 blog work has social proof and traffic seeding ready",
          acceptanceCriteria: [
            "1 LinkedIn post, 1 Twitter thread, 1 Reddit post drafted",
            "Each draft includes required disclosure language",
            "Angles pulled from content-angle-ranker top-3, not generic",
          ],
          priority: "must",
          storyPoints: 5,
          status: "in-progress",
          startDate: d(-10),
          dueDate: d(2),
          estimatedDays: 12,
        },
        {
          id: "us-3",
          title: "Publish comparison article (S3)",
          asA: "SEO specialist",
          iWant: "a Rocket Money vs YNAB comparison-post-writer article that passes seo-audit at ≥8/10",
          soThat: "the article ranks and converts without a manual rewrite cycle",
          acceptanceCriteria: [
            "All 10 SEO dimensions scored, none below 6",
            "Affiliate disclosure above the fold",
            "Internal link to the Empower listicle once published",
          ],
          priority: "must",
          storyPoints: 8,
          status: "in-progress",
          startDate: d(-7),
          dueDate: d(5),
          estimatedDays: 12,
        },
        {
          id: "us-4",
          title: "Build bonus-stacked landing page (S4)",
          asA: "affiliate marketer",
          iWant: "a landing-page-creator + bonus-stack-builder page bundling all 3 programs",
          soThat: "visitors get one destination instead of 3 separate outbound links",
          acceptanceCriteria: [
            "AIDA structure present (attention, interest, desire, action)",
            "Bonus stack lists at least 2 exclusive bonuses per program",
            "Guarantee-generator language included for risk reversal",
          ],
          priority: "should",
          storyPoints: 8,
          status: "backlog",
          startDate: d(6),
          dueDate: d(20),
          estimatedDays: 14,
        },
        {
          id: "us-5",
          title: "Deploy owned bio-link and email drip (S5)",
          asA: "affiliate marketer",
          iWant: "bio-link-deployer and email-drip-sequence live on an owned domain",
          soThat: "traffic and list-building aren't dependent on a third-party shortener",
          acceptanceCriteria: [
            "Bio link resolves on campaign-owned domain",
            "5-7 email drip sequence live with 1 email per program minimum",
            "All links carry UTM tags per FR-ANLY-001",
          ],
          priority: "should",
          storyPoints: 5,
          status: "backlog",
          startDate: d(21),
          dueDate: d(35),
          estimatedDays: 14,
        },
        {
          id: "us-6",
          title: "Stand up conversion tracking and first A/B test (S6)",
          asA: "SEO specialist",
          iWant: "conversion-tracker live on all links plus an ab-test-generator headline test on the landing page",
          soThat: "we have attribution data before committing to S7 automation",
          acceptanceCriteria: [
            "UTM + pixel tracking confirmed firing on all 3 programs",
            "A/B test reaches n≥1000 impressions per variant before calling a winner",
            "Weekly performance-report generated automatically",
          ],
          priority: "should",
          storyPoints: 8,
          status: "backlog",
          startDate: d(36),
          dueDate: d(52),
          estimatedDays: 16,
        },
        {
          id: "us-7",
          title: "Compliance sign-off before automation (S7 gate)",
          asA: "compliance reviewer",
          iWant: "a compliance-checker pass across every live asset before email-automation-builder goes live",
          soThat: "scaling distribution doesn't scale a disclosure violation",
          acceptanceCriteria: [
            "FTC disclosure verified on every post, article, and landing page",
            "Platform-specific ToS checked (Reddit self-promo ratio, TikTok caption rules)",
            "Sign-off logged before S7 kicks off",
          ],
          priority: "must",
          storyPoints: 3,
          status: "backlog",
          startDate: d(50),
          dueDate: d(58),
          estimatedDays: 8,
        },
        {
          id: "us-8",
          title: "Campaign retrospective and category design (Meta/S8)",
          asA: "affiliate marketer",
          iWant: "self-improver retrospective output plus a category-designer pass on the winning program",
          soThat: "the next niche cycle starts from documented learnings instead of from zero",
          acceptanceCriteria: [
            "Retrospective covers what worked, what didn't, per stage S1-S7",
            "Category-designer identifies 1 differentiated angle not yet claimed by competitors",
            "Findings logged to Evolution Log",
          ],
          priority: "could",
          storyPoints: 5,
          status: "backlog",
          startDate: d(60),
          dueDate: d(75),
          estimatedDays: 15,
        },
      ],
    },
    { headers: authHeader }
  );

  const projectId: string = project.id;

  const tryPost = async (url: string, body: object) => {
    try {
      const { data } = await axios.post(url, body, { headers: authHeader });
      return data;
    } catch {
      return null;
    }
  };

  // ── 2. Requirements ──────────────────────────────────────────────────────────
  const requirements = [
    {
      title: "Affitor Score threshold for program inclusion",
      raw_text: "A program must not be added to the active campaign unless its Affitor Score (Search Demand 30%, Program Availability 30%, Competition 25%, Content Potential 15%) is ≥ 8.0 overall.",
      req_type: "functional", priority: "must", domain: "research",
    },
    {
      title: "Minimum commission and cookie window",
      raw_text: "Every sourced program must offer either ≥20% recurring commission or ≥$20 flat CPA, with a cookie window of at least 30 days, verified on list.affitor.com.",
      req_type: "functional", priority: "must", domain: "validation",
    },
    {
      title: "FTC disclosure on every published asset",
      raw_text: "Every post, article, video caption, and landing page containing an affiliate link must carry a clear and conspicuous disclosure before the link, per FTC Endorsement Guides.",
      req_type: "non-functional", priority: "must", domain: "compliance",
    },
    {
      title: "SEO scorecard pass bar",
      raw_text: "Every published blog asset must score ≥8/10 average across the 10-dimension SEO scorecard, with no individual dimension below 6, before it counts toward S3 completion.",
      req_type: "non-functional", priority: "must", domain: "quality",
    },
    {
      title: "UTM tagging before distribution",
      raw_text: "No link may be distributed via bio-link-deployer, email-drip-sequence, or social channels until it carries a full UTM tag set generated by conversion-tracker.",
      req_type: "constraint", priority: "must", domain: "analytics",
    },
    {
      title: "Compliance gate before automation scale-up",
      raw_text: "email-automation-builder and any S7 branching automation must not go live until Compliance Reviewer has signed off on disclosure and platform ToS for every asset in the flow.",
      req_type: "constraint", priority: "must", domain: "compliance",
    },
  ];

  for (const req of requirements) {
    await tryPost(`${BASE}/api/tools/requirements`, { ...req, project_id: projectId });
  }

  // ── 3. CAPA entries ───────────────────────────────────────────────────────
  const capas = [
    {
      project_id: projectId,
      title: "Missing disclosure caught on TikTok script draft",
      description: "QA review of the tiktok-script-writer output for Rocket Money found the affiliate disclosure only in the video description, not spoken or on-screen within the first few seconds as required.",
      root_cause: "Script template used the general social-disclosure snippet instead of the platform-specific TikTok rule (caption + on-screen + spoken).",
      corrective_action: "Rewrite the script to include a spoken disclosure in the first 3 seconds and an on-screen caption for the full duration.",
      preventive_action: "Add a platform-specific disclosure checklist item to compliance-checker for every TikTok/video asset before it's marked ready to publish.",
      status: "open",
      priority: "high",
      due_date: d(4),
      raised_by: "Compliance Reviewer",
    },
    {
      project_id: projectId,
      title: "Conversion pixel double-fired on landing page A/B test",
      description: "conversion-tracker pixel fired twice per session on variant B of the landing page, inflating conversion counts by roughly 2x before it was caught.",
      root_cause: "Pixel script was included both in the base landing-page-creator template and again in the bonus-stack-builder section, causing duplicate firing on page load.",
      corrective_action: "Remove the duplicate pixel from the bonus-stack section; confirm single-fire via browser network inspection before test resumes.",
      preventive_action: "Add a pre-launch checklist step: verify exactly one conversion-tracker pixel instance per page before any A/B test goes live.",
      status: "open",
      priority: "high",
      due_date: d(6),
      raised_by: "SEO Specialist",
    },
  ];

  for (const capa of capas) {
    await tryPost(`${BASE}/api/tools/capa`, capa);
  }

  // ── 4. Suppliers (Affitor programs + platform) ────────────────────────────
  const suppliers = [
    {
      project_id: projectId,
      name: "Rocket Money",
      category: "Affiliate Program",
      risk_tier: "minor",
      contact_email: "partners@rocketmoney.com",
      notes: "25% recurring, 45-day cookie. Affitor Score 8.6. Verified active on list.affitor.com.",
    },
    {
      project_id: projectId,
      name: "YNAB (You Need A Budget)",
      category: "Affiliate Program",
      risk_tier: "minor",
      contact_email: "affiliates@ynab.com",
      notes: "$20 flat per trial-to-paid conversion, 30-day cookie. Affitor Score 7.9.",
    },
    {
      project_id: projectId,
      name: "Empower (Personal Capital)",
      category: "Affiliate Program",
      risk_tier: "minor",
      contact_email: "affiliates@empower.com",
      notes: "$100 flat CPA, 60-day cookie. Highest payout of the 3, lower volume niche fit. Affitor Score 7.7.",
    },
    {
      project_id: projectId,
      name: "list.affitor.com",
      category: "Research Platform",
      risk_tier: "minor",
      contact_email: "",
      notes: "Source of truth for program discovery, Affitor Score inputs, commission terms, and cookie-window data. Re-check cadence: every 30 days per FR-ANLY-003.",
    },
  ];

  for (const supplier of suppliers) {
    await tryPost(`${BASE}/api/tools/suppliers`, supplier);
  }

  // ── 5. Test Cases ─────────────────────────────────────────────────────────
  const testCases = [
    {
      project_id: projectId,
      title: "SEO audit — Rocket Money vs YNAB comparison article",
      description: "Run the 10-dimension SEO scorecard against the comparison-post-writer draft before it clears S3.",
      steps: [
        "Open seo-audit skill against the draft article",
        "Confirm all 10 dimensions scored",
        "Flag any dimension below 6/10",
        "Verify FTC disclosure appears above the fold",
      ],
      expected_result: "Overall score ≥8/10, no dimension below 6, disclosure present and conspicuous.",
      priority: "must",
      status: "pending",
    },
    {
      project_id: projectId,
      title: "A/B test — bonus-stacked landing page headline",
      description: "Validate ab-test-generator variants reach a statistically valid sample before a winner is called.",
      steps: [
        "Generate 3 headline variants via ab-test-generator",
        "Deploy via landing-page-creator with even traffic split",
        "Track impressions and conversions via conversion-tracker",
        "Confirm n≥1000 impressions per variant before declaring a winner",
      ],
      expected_result: "3 variants live, single-fire pixel confirmed, winner only called after sample threshold met.",
      priority: "must",
      status: "pending",
    },
    {
      project_id: projectId,
      title: "Automation branch test — email-automation-builder conditions",
      description: "Confirm branching logic in the S7 email automation correctly routes recipients based on click behavior on the S5 drip sequence.",
      steps: [
        "Send test batch through the 5-7 email drip sequence",
        "Trigger a click on the Rocket Money link only",
        "Verify automation routes that recipient to the Rocket Money-specific branch",
        "Verify a non-clicker is routed to the re-engagement branch",
      ],
      expected_result: "Branching routes recipients correctly based on click behavior with no cross-branch leakage.",
      priority: "should",
      status: "pending",
    },
  ];

  for (const tc of testCases) {
    await tryPost(`${BASE}/api/tools/test-cases`, tc);
  }

  // ── 6. Evolution Log ──────────────────────────────────────────────────────
  const evolutionEntries = [
    {
      project_id: projectId,
      change_type: "scope_add",
      title: "Add Empower (Personal Capital) as third sourced program",
      description: "Initial S1 research surfaced only Rocket Money and YNAB. A follow-up list.affitor.com search for high-CPA finance programs surfaced Empower at $100 flat CPA.",
      requested_by: "Affiliate Marketer",
      impact: "Adds 1 program to validate, 1 additional content angle. Comparison article scope widened from 2-way to 3-way.",
      status: "approved",
      created_at: d(-18),
    },
    {
      project_id: projectId,
      change_type: "requirement_change",
      title: "Affitor Score threshold raised from 7.0 to 8.0",
      description: "Initial validation bar of 7.0 overall Affitor Score passed too many marginal programs through to S1 sign-off, diluting content focus.",
      requested_by: "Affiliate Marketer",
      impact: "Tightens candidate pool from 6 sourced programs down to the 3 that cleared the raised bar.",
      status: "approved",
      created_at: d(-19),
    },
    {
      project_id: projectId,
      change_type: "scope_remove",
      title: "Drop TikTok as a primary S2 channel for this cycle",
      description: "Disclosure compliance overhead (spoken + on-screen + caption, per CAPA-1) made TikTok higher-effort than LinkedIn/Reddit for the current team size.",
      requested_by: "Content Team",
      impact: "Reduces S2 content workload by ~30%. TikTok kept as a backlog channel for Meta-stage retrospective consideration.",
      status: "approved",
      created_at: d(-5),
    },
  ];

  for (const entry of evolutionEntries) {
    await tryPost(`${BASE}/api/tools/evolution-logs`, entry);
  }

  // ── 7. Knowledge Base documents ───────────────────────────────────────────
  const kbDocs = [
    {
      project_id: projectId,
      title: "Affitor Score Methodology",
      doc_type: "reference",
      source: "list.affitor.com scoring model",
      content: `# Affitor Score

## The 4 Dimensions
| Dimension | Weight | What it measures |
|---|---|---|
| Search Demand | 30% | Google Trends, Ahrefs volume, SERP inspection |
| Program Availability | 30% | Number and quality of programs on list.affitor.com |
| Competition Level | 25% | SERP crowding, domain authority of top-ranking pages |
| Content Potential | 15% | Breadth of viable content angles and formats |

## Pass Bar
This campaign requires an overall Affitor Score ≥ 8.0 before a niche or program is committed to S2+ work. See requirement "Affitor Score threshold for program inclusion".

## Re-check Cadence
Programs are re-scored every 30 days — commission rates and cookie windows on list.affitor.com are not static.`,
    },
    {
      project_id: projectId,
      title: "S1-S8 Stage Playbook",
      doc_type: "technical",
      source: "Affitor skill stage reference",
      content: `# Stage Playbook

| Stage | Name | Core Skills |
|---|---|---|
| S1 | Research & Discovery | affiliate-program-search, niche-opportunity-finder, commission-calculator |
| S2 | Content Creation | viral-post-writer, twitter-thread-writer, tiktok-script-writer |
| S3 | Blog & SEO | affiliate-blog-builder, comparison-post-writer, keyword-cluster-architect |
| S4 | Offers & Landing Pages | landing-page-creator, bonus-stack-builder, guarantee-generator |
| S5 | Distribution & Deployment | bio-link-deployer, email-drip-sequence, social-media-scheduler |
| S6 | Analytics & Optimization | conversion-tracker, ab-test-generator, seo-audit |
| S7 | Automation & Scale | email-automation-builder, multi-program-manager, content-repurposer |
| S8 | Meta | skill-finder, funnel-planner, self-improver, category-designer |

Each stage gates the next — see RACI matrix for sign-off owners at each gate.`,
    },
    {
      project_id: projectId,
      title: "FTC Disclosure Requirements — Affiliate Content",
      doc_type: "policy",
      source: "FTC Endorsement Guides",
      content: `# FTC Affiliate Disclosure Rules

- Disclosure must be "clear and conspicuous" — not buried in a footer or About page.
- Must appear before the affiliate link, not after.
- Reddit: disclosure required in the post body, not just a bio link.
- TikTok/video: disclosure required in the caption AND spoken/on-screen within the first few seconds (see CAPA-1).
- Applies to every asset before it counts as compliant for the S7 automation gate.`,
    },
  ];

  for (const doc of kbDocs) {
    await tryPost(`${BASE}/api/tools/documents`, doc);
  }

  // ── 8. Compliance Rules ───────────────────────────────────────────────────
  const rules = [
    {
      project_id: projectId,
      name: "FTC — Affiliate Link Disclosure",
      description: "Every piece of published content containing an affiliate link must carry a clear and conspicuous disclosure appearing before the link, per FTC Endorsement Guides.",
      domain: "marketing",
      market: "US",
      channel: "content",
      contains_phi: false,
    },
    {
      project_id: projectId,
      name: "Platform ToS — TikTok video disclosure",
      description: "Video content must disclose the affiliate relationship both in the caption and spoken/on-screen within the first few seconds. Caption-only disclosure is non-compliant (see CAPA-1).",
      domain: "marketing",
      market: "global",
      channel: "tiktok",
      contains_phi: false,
    },
    {
      project_id: projectId,
      name: "Affitor Program Terms — Re-verification",
      description: "Commission rate and cookie window for every live program must be re-verified against list.affitor.com every 30 days; terms can change without notice.",
      domain: "operations",
      market: "global",
      channel: "internal",
      contains_phi: false,
    },
  ];

  for (const rule of rules) {
    await tryPost(`${BASE}/api/tools/rules`, rule);
  }

  return projectId;
}
